'use client'

import {
  Body,
  Card,
  Container,
  GlassCard,
  GlassCardBody,
  Pretitle,
  Reveal,
  Section,
  SectionHeader,
  Stagger,
  ThemeImageClient,
} from 'design-studio'
import { ShikiCode } from '@/components/ui/shiki-code'
import {
  SL_GLASS_BODY,
  SL_GLASS_SURFACE,
  SL_GLASS_TITLE,
} from '@/components/theme'

export function SectionSpecConstruction() {
  return (
    <Section
      id="spec-construction"
      className="relative scroll-mt-19 bg-zinc-200 py-16 backdrop-blur-md sm:pt-24 sm:pb-32 dark:bg-slate-800/10"
    >
      <Container className="relative z-10 max-w-6xl">
        <Stagger className="space-y-10">
          <Reveal>
            <SectionHeader
              pretitle="Specification construction"
              pretitleColor="text-teal-500"
              title="How the Specification Is Constructed"
              description="In SpecLogician, the specification is a shared mathematical model discovered from the system and refined into a form that can be visualized, reasoned about, and kept aligned over time."
            />
          </Reveal>

          <Reveal>
            <Body
              size="sm"
              tone="muted"
              className="mx-auto max-w-4xl text-center"
            >
              SpecLogician uses{' '}
              <a
                href="https://www.imandrax.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline underline-offset-4 dark:text-blue-400"
              >
                ImandraX
              </a>{' '}
              and the Imandra Modeling Language to discover and represent system
              behavior explicitly, then reason over that specification to check
              consistency, find edge cases, generate high-value tests, and
              connect runtime evidence back to system semantics.
            </Body>
          </Reveal>

          <Reveal className="grid gap-8 lg:grid-cols-[1fr_1fr]">
            <div className="min-w-0 space-y-6">
              <GlassCard
                title="Domain model"
                titleClassName={SL_GLASS_TITLE}
                className={SL_GLASS_SURFACE}
              >
                <GlassCardBody className={`mb-4 ${SL_GLASS_BODY}`}>
                  The domain model defines the core state and action types of
                  the system.
                </GlassCardBody>
                <ShikiCode
                  lang="ocaml"
                  colorScheme="dark"
                  code={`type state = {\n  logged_in : bool;\n  login_count : int;\n}\n\ntype action =\n  | Login\n  | Logout`}
                />
              </GlassCard>

              <GlassCard
                title="Predicates and transition"
                titleClassName={SL_GLASS_TITLE}
                className={SL_GLASS_SURFACE}
              >
                <GlassCardBody className={`mb-4 ${SL_GLASS_BODY}`}>
                  Predicates describe properties we care about, and the
                  transition function describes how the system evolves.
                </GlassCardBody>
                <ShikiCode
                  lang="ocaml"
                  colorScheme="dark"
                  code={`let step (s : state) (a : action) : state =\n  match a with\n  | Login ->\n      { logged_in = true;\n        login_count = s.login_count + 1 }\n  | Logout ->\n      { logged_in = false;\n        login_count = s.login_count }\n\nlet is_logged_in (s : state) : bool =\n  s.logged_in\n\nlet has_logged_in_at_least_once (s : state) : bool =\n  s.login_count > 0`}
                />
              </GlassCard>
            </div>

            <GlassCard
              title="A single scenario"
              titleClassName={SL_GLASS_TITLE}
              className={`min-w-0 ${SL_GLASS_SURFACE}`}
            >
              <GlassCardBody className={`mb-4 ${SL_GLASS_BODY}`}>
                Scenarios capture intended behavior in structured slices
                grounded in the domain model.
              </GlassCardBody>

              <Card
                tone="muted"
                radius="lg"
                padding="md"
                className="border-zinc-200 bg-zinc-50 dark:border-slate-700 dark:bg-slate-900/40"
              >
                <Pretitle tone="default" className="tracking-wider">
                  Example scenario
                </Pretitle>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300/90">
                  <strong className="text-slate-900 dark:text-slate-100">
                    Given:
                  </strong>{' '}
                  the user is logged out
                  <br />
                  <strong className="text-slate-900 dark:text-slate-100">
                    When:
                  </strong>{' '}
                  a <code>Login</code> action occurs
                  <br />
                  <strong className="text-slate-900 dark:text-slate-100">
                    Then:
                  </strong>{' '}
                  <code>is_logged_in</code> becomes true and{' '}
                  <code>has_logged_in_at_least_once</code> holds
                </p>
              </Card>

              <div className="mt-6 space-y-3">
                <Body size="sm" tone="muted" className="font-normal">
                  <strong className="text-slate-900 dark:text-slate-100">
                    State
                  </strong>{' '}
                  describes what the system currently knows.
                </Body>
                <Body size="sm" tone="muted" className="font-normal">
                  <strong className="text-slate-900 dark:text-slate-100">
                    Actions
                  </strong>{' '}
                  describe events that can change the system.
                </Body>
                <Body size="sm" tone="muted" className="font-normal">
                  <strong className="text-slate-900 dark:text-slate-100">
                    Predicates
                  </strong>{' '}
                  describe properties we want to reason about.
                </Body>
                <Body size="sm" tone="muted" className="font-normal">
                  <strong className="text-slate-900 dark:text-slate-100">
                    Scenarios
                  </strong>{' '}
                  turn intended behavior into analyzable structure.
                </Body>
              </div>

              <Body size="sm" tone="muted" className="mt-6">
                Real systems scale this up by linking many such domain models,
                predicates, scenarios, requirements, tests, logs, and traces
                into one evolving mathematical specification, then using
                ImandraX to reason over it.
              </Body>
            </GlassCard>
          </Reveal>

          <Reveal className="mt-8 space-y-12">
            <div className="mx-auto max-w-3xl text-center">
              <Pretitle tone="default" className="text-teal-500">
                Live in the dashboard
              </Pretitle>
              <Body size="sm" tone="muted" className="mt-3">
                As the model grows, the dashboard renders it live — an aggregate
                summary, every transition with its artifacts and source, and
                every scenario with its predicates and links.
              </Body>
            </div>

            <figure className="flex flex-col gap-3">
              <ThemeImageClient
                lightSrc="/images/dashboard/sl-spec-summary-light.png"
                darkSrc="/images/dashboard/sl-spec-summary-dark.png"
                alt="Dashboard Summary tab with base model validity, predicate / transition / scenario counts, scenario flags, and state reachability summary"
                className="w-full overflow-hidden rounded-xl opacity-90 shadow-md"
                width={2024}
                height={872}
              />
              <Body size="sm" tone="muted" className="text-center">
                Summary tab — validity, counts, scenario flags, reachability.
              </Body>
            </figure>

            <figure className="flex flex-col gap-3">
              <ThemeImageClient
                lightSrc="/images/dashboard/sl-transition-closeup-light.png"
                darkSrc="/images/dashboard/sl-transition-closeup-dark.png"
                alt="A row from the Transitions tab — name, validity badge, linked artifacts, scenarios, and IML source code"
                className="w-full overflow-hidden rounded-xl opacity-90 shadow-md"
                width={2252}
                height={274}
              />
              <Body size="sm" tone="muted" className="text-center">
                A row from the Transitions tab — name, validity, linked
                artifacts, scenarios, source IML.
              </Body>
            </figure>

            <figure className="mx-auto flex max-w-sm flex-col gap-3">
              <ThemeImageClient
                lightSrc="/images/dashboard/sl-scenario-closeup-light.png"
                darkSrc="/images/dashboard/sl-scenario-closeup-dark.png"
                alt="A scenario card showing given state predicates, when action predicates, then transitions, and linked artifacts"
                className="w-full overflow-hidden rounded-xl opacity-90 shadow-md"
                width={580}
                height={544}
              />
              <Body size="sm" tone="muted" className="text-center">
                A scenario card — given / when / then chips and linked
                artifacts.
              </Body>
            </figure>
          </Reveal>
        </Stagger>
      </Container>
    </Section>
  )
}
